"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { useDict } from "@/components/i18n/DictProvider";
import { langPath, type Lang } from "@/lib/i18n";
import { saveModule } from "@/lib/lecturehub/module-actions";

/**
 * Save on an article — the control every other module detail already has
 * (`MediaSidebar`, `EventStartCard`), in the sticky reader strip so it stays
 * reachable while reading instead of scrolling away with the lede.
 *
 * Posts only: a Članek is a `Module` and `accountSaveModule` takes a module id,
 * while a news item is a `CommunicationPost` — a different type the account
 * cannot bookmark at all. That is why `NewsArticle` gets no save button, and
 * why this lives under `components/post/`.
 *
 * Optimistic, and reverts on failure: the toggle is the whole feedback, so a
 * rejected save that left the icon filled would be a lie. A guest is sent to
 * login with the article as `redirect` rather than being shown a control that
 * silently does nothing (the action itself calls `verifySession()`; this is the
 * courtesy, not the gate).
 */
export function SavePostButton({
  lang,
  moduleId,
  initialSaved,
  isAuthed,
  compact = false,
  className,
}: {
  lang: Lang;
  moduleId: number;
  initialSaved: boolean;
  isAuthed: boolean;
  /** Icon only, sized and coloured as the reader strip's other buttons — the
   *  strip is a row of icons, and a lone worded button would break it. The
   *  label stays as `aria-label` and `title`. */
  compact?: boolean;
  className?: string;
}) {
  const t = useDict();
  const router = useRouter();
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();

  function toggle() {
    if (!isAuthed) {
      router.push(
        `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`,
      );
      return;
    }
    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const r = await saveModule(moduleId, next);
      if (!r.ok) setSaved(!next);
    });
  }

  const label = saved ? t.detail.saved : t.detail.save;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      aria-pressed={saved}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      className={cn(
        compact
          ? // The reader strip's button vocabulary (ReaderTools.iconBtn).
            "focus-visible:ring-ring bg-muted hover:bg-border/60 inline-flex size-9 items-center justify-center rounded-md transition-colors pointer-coarse:size-11 focus-visible:ring-2 focus-visible:outline-none"
          : // 32 px drawn; the pseudo-element takes it to 44 px on a touch
            // screen without moving the row, as the listing chips do.
            "relative inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium transition-colors pointer-coarse:before:absolute pointer-coarse:before:inset-x-0 pointer-coarse:before:-inset-y-2 pointer-coarse:before:content-['']",
        saved ? "text-accent" : "text-muted-foreground hover:text-foreground",
        pending && "opacity-60",
        className,
      )}
    >
      {saved ? (
        <BookmarkCheck className="size-4" aria-hidden />
      ) : (
        <Bookmark className="size-4" aria-hidden />
      )}
      {compact ? null : label}
    </button>
  );
}
